import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useShallowSidebarStore } from "../../../store/sidebar-store";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck,
  ClipboardCheck,
  Clock,
  EllipsisVertical,
  File,
  FileImage,
  FileText,
  Maximize2,
  MessageSquare,
  Package,
  Phone,
  Pill,
  DollarSign,
  RefreshCw,
  Ticket,
  UserRound,
  Users,
  HeartPulse,
} from "lucide-react";
import { useState } from "react";
import { ContactsDialog } from "../../ContactsDialog";
import { convertStringToContactDetailsSection } from "./utils";
import { useAuth, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { useCallingShallowStore } from "../../../store/calling-store";

export const ContactDetailsSidebarHeaderContent = () => {
  const { t } = useTranslation("contacts");
  const { contentTypeData, setContentTypeData, toggleIsOpen } = useShallowSidebarStore(
    "contentTypeData",
    "setContentTypeData",
    "toggleIsOpen"
  );
  const selectedSection = contentTypeData.ContactDetails?.selectedSection ?? "Information";
  const enduserId = contentTypeData.ContactDetails?.enduserId ?? "";

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const { openDialer } = useCallingShallowStore("openDialer");

  const { session } = useAuth();
  const navigate = useNavigate();
  const hasTwilioAccess = !!(session?.userInfo?.twilioNumber || session?.userInfo?.orgTwilioNumber);

  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !!enduserId,
  });
  const enduser = enduserQuery.data;

  return (
    <div className="flex items-center gap-1 flex-grow">
      <Select
        name="sidebar-contact-details-section-select"
        value={selectedSection}
        onValueChange={(selectedSection) => {
          const convertedSelection = convertStringToContactDetailsSection(selectedSection);
          if (!convertedSelection) return;
          setContentTypeData((prev) => ({
            ...prev,
            ContactDetails: {
              enduserId: contentTypeData.ContactDetails?.enduserId ?? "",
              selectedSection: convertedSelection,
            },
          }));
        }}
      >
        <SelectTrigger>
          <SelectValue className="flex" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Information" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <UserRound />
              {t("Information")}
            </div>
          </SelectItem>
          <SelectItem value="RelatedContacts" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <Users />
              {t("Related Contacts")}
            </div>
          </SelectItem>
          <SelectItem value="Timeline" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <Clock />
              {t("Timeline")}
            </div>
          </SelectItem>
          <SelectItem value="Tickets" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <Ticket />
              {t("Tickets")}
            </div>
          </SelectItem>
          <SelectItem value="Events" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <CalendarCheck />
              {t("Events")}
            </div>
          </SelectItem>
          <SelectItem value="Eligibility" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <ClipboardCheck />
              {t("Eligibility")}
            </div>
          </SelectItem>
          <SelectItem value="Orders" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <Package />
              {t("Orders")}
            </div>
          </SelectItem>
          <SelectItem value="Encounters" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <RefreshCw />
              {t("Encounters")}
            </div>
          </SelectItem>
          <SelectItem value="Files" className="cursor-pointer">
            <div className="flex gap-2 items-center">
              <File />
              {t("Files")}
            </div>
          </SelectItem>
          <SelectItem value="Forms" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <FileText />
              {t("Forms")}
            </div>
          </SelectItem>
          <SelectItem value="Content" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <FileImage />
              {t("Content")}
            </div>
          </SelectItem>
          <SelectItem value="Medications" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <Pill />
              {t("Medications")}
            </div>
          </SelectItem>
          <SelectItem value="Payment" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <DollarSign />
              {t("Payment")}
            </div>
          </SelectItem>
          <SelectItem value="Vitals" className="cursor-pointer">
            <div className="flex gap-2 items-center cursor-pointer">
              <HeartPulse />
              {t("Vitals")}
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t("Contact actions")}>
              <EllipsisVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasTwilioAccess && enduser?.phone && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => openDialer({ enduser: enduser ?? undefined })}
              >
                <Phone className="mr-2 h-4 w-4" />
                {t("Call")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => {
                const params = new URLSearchParams({ "composeTo.enduserId": enduserId });
                if (enduser?.phone) params.set("composeTo.number", enduser.phone);
                navigate(`/messaging/inbox/sms/new?${params.toString()}`);
              }}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              {t("Message")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setDialogIsOpen(true);
            toggleIsOpen();
          }}
          hoverLabel={t("Open in dialog")}
        >
          <Maximize2 />
        </Button>
      </div>
      <ContactsDialog
        key={`${contentTypeData.ContactDetails?.enduserId ?? ""}-${
          contentTypeData.ContactDetails?.selectedSection ?? ""
        }`}
        contactId={contentTypeData.ContactDetails?.enduserId ?? ""}
        open={dialogIsOpen}
        setOpen={setDialogIsOpen}
        initialSection={selectedSection}
      />
    </div>
  );
};
